import { useState } from "react";
import api, { downloadUrl, formatApiErrorDetail } from "../../lib/api";
import { typeMeta, GRADED_TYPES, embedUrl } from "./itemTypes";
import FlashcardViewer from "./FlashcardViewer";
import VideoEmbed from "./VideoEmbed";
import ItemForm from "./ItemForm";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent, DropdownMenuSeparator,
} from "../ui/dropdown-menu";
import { MoreHorizontal, ExternalLink, Download, Eye, EyeOff, Pencil, CopyPlus, Trash2, ArrowUp, ArrowDown, FolderInput, GraduationCap, ClipboardList, PlayCircle, WalletCards, CalendarDays } from "lucide-react";

function StatCell({ label, value, testId }) {
  return (
    <div className="text-center min-w-[66px]" data-testid={testId}>
      <p className="text-sm font-bold leading-tight">{value ?? "—"}</p>
      <p className="text-[10px] uppercase tracking-wider text-[var(--muted-text)]">{label}</p>
    </div>
  );
}

function Tags({ item }) {
  const [showAll, setShowAll] = useState(false);
  const tags = item.tags || [];
  if (tags.length === 0) return null;
  const shown = showAll ? tags : tags.slice(0, 1);
  return (
    <span className="inline-flex items-center gap-1 flex-wrap">
      {shown.map((t) => (
        <span key={t} data-testid={`item-tag-${item.id}-${t}`} className="px-2 py-0.5 rounded-full text-[10px] font-semibold border border-[var(--gold)]/60 text-[var(--gold-soft)]">{t}</span>
      ))}
      {tags.length > 1 && (
        <button
          data-testid={`item-tags-more-${item.id}`}
          onClick={() => setShowAll((s) => !s)}
          title={tags.join(", ")}
          className="px-1.5 py-0.5 rounded-full text-[10px] font-bold border border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold)] hover:text-[var(--navy-on-gold)] transition-colors"
        >
          {showAll ? "−" : `+${tags.length - 1}`}
        </button>
      )}
    </span>
  );
}

function studentStatus(item) {
  const sub = item.my_submission;
  if (!sub) return "Not submitted";
  if (!sub.graded) return "Submitted";
  return `${sub.score}%`;
}

// A single item row inside a section.
export default function ItemRow({ item, moduleId, sectionId, sections, isInstructor, index, total, onChanged, onError, onGoToExams, onGoToProjects, materials, exams, projects, skillTags }) {
  const meta = typeMeta(item.type);
  const [viewer, setViewer] = useState(null); // "cards" | "video" | "edit"
  const graded = GRADED_TYPES.includes(item.type);
  const counts = graded && item.counts_toward_grade !== false;
  const video = item.type === "watch" && item.url ? embedUrl(item.url) : null;
  const isPdf = item.material?.filename?.toLowerCase().endsWith(".pdf");

  const act = async (fn) => {
    try { await fn(); onChanged(); }
    catch (e) { onError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const move = (toIndex, toSection = sectionId) =>
    act(() => api.post(`/modules/${moduleId}/items/${item.id}/move`, { to_section_id: toSection, to_index: toIndex }));
  const toggleHidden = () => act(() => api.patch(`/modules/${moduleId}/items/${item.id}`, { hidden: !item.hidden }));
  const duplicate = () => act(() => api.post(`/modules/${moduleId}/items/${item.id}/duplicate`));
  const remove = () => {
    if (!window.confirm(`Delete "${item.title}" from this section? (Linked exams/projects/materials are NOT deleted.)`)) return;
    act(() => api.delete(`/modules/${moduleId}/items/${item.id}`));
  };

  const primaryAction = () => {
    if (item.type === "flashcards") {
      return <button data-testid={`item-open-${item.id}`} onClick={() => setViewer("cards")} className="nb-btn nb-btn-white text-sm flex items-center gap-1.5"><WalletCards className="w-4 h-4" /> Study</button>;
    }
    if (item.type === "quiz") {
      return <button data-testid={`item-open-${item.id}`} onClick={onGoToExams} className="nb-btn nb-btn-yellow text-sm flex items-center gap-1.5"><GraduationCap className="w-4 h-4" /> Open</button>;
    }
    if (item.type === "assignment") {
      return <button data-testid={`item-open-${item.id}`} onClick={onGoToProjects} className="nb-btn nb-btn-yellow text-sm flex items-center gap-1.5"><ClipboardList className="w-4 h-4" /> Open</button>;
    }
    if (video) {
      return <button data-testid={`item-open-${item.id}`} onClick={() => setViewer("video")} className="nb-btn nb-btn-white text-sm flex items-center gap-1.5"><PlayCircle className="w-4 h-4" /> Watch</button>;
    }
    if (item.url) {
      return <a data-testid={`item-open-${item.id}`} href={item.url} target="_blank" rel="noreferrer" className="nb-btn nb-btn-white text-sm flex items-center gap-1.5"><ExternalLink className="w-4 h-4" /> Open</a>;
    }
    if (item.material_id) {
      const href = downloadUrl(`/materials/${item.material_id}/download${item.type === "read" && isPdf ? "?inline=1" : ""}`);
      return (
        <a data-testid={`item-open-${item.id}`} href={href} target="_blank" rel="noreferrer" className="nb-btn nb-btn-white text-sm flex items-center gap-1.5">
          {item.type === "read" && isPdf ? <><Eye className="w-4 h-4" /> View</> : <><Download className="w-4 h-4" /> Download</>}
        </a>
      );
    }
    return null;
  };

  return (
    <li data-testid={`module-item-${item.id}`} className={`py-3 flex items-center justify-between gap-3 ${item.hidden ? "opacity-55" : ""}`}>
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <div className="w-9 h-9 rounded-lg border-2 border-[var(--gold)] bg-[var(--blue)] flex items-center justify-center shrink-0">
          <meta.icon className="w-4 h-4" />
        </div>
        <div className="min-w-0">
          <p className="font-semibold truncate text-sm md:text-base">
            <span className="text-[var(--gold)]">{meta.label}:</span> {item.title}
            {item.hidden && isInstructor && <span data-testid={`item-hidden-badge-${item.id}`} className="ml-2 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[var(--muted-text)]/25 text-[var(--gold-soft)]">Hidden</span>}
          </p>
          <div className="flex items-center gap-2 flex-wrap mt-0.5">
            {counts && (
              <span data-testid={`item-counts-badge-${item.id}`} className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-orange-400">
                <span className="w-2 h-2 rounded-full bg-orange-500 inline-block" /> Counts towards grade
              </span>
            )}
            {graded && !counts && (
              <span data-testid={`item-practice-badge-${item.id}`} className="text-[10px] font-bold uppercase tracking-wider text-[var(--muted-text)]">Practice only</span>
            )}
            {item.due_date && (
              <span data-testid={`item-due-${item.id}`} className="inline-flex items-center gap-1 text-[11px] text-[var(--gold-soft)]"><CalendarDays className="w-3 h-3" /> Due {item.due_date.slice(0, 10)}</span>
            )}
            <Tags item={item} />
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 shrink-0">
        {graded && (
          <div className="hidden sm:flex items-center gap-3 border-r-2 border-[var(--gold)]/30 pr-3">
            <StatCell label="points" value={item.points} testId={`item-points-${item.id}`} />
            {isInstructor ? (
              <>
                <StatCell label="% submitted" value={item.stats && item.stats.submitted_count > 0 ? `${item.stats.pct_submitted}%` : null} testId={`item-pct-submitted-${item.id}`} />
                <StatCell label="avg score" value={item.stats?.avg_score != null ? `${item.stats.avg_score}%` : null} testId={`item-avg-score-${item.id}`} />
              </>
            ) : (
              <StatCell label="your score" value={studentStatus(item)} testId={`item-my-status-${item.id}`} />
            )}
          </div>
        )}
        {primaryAction()}
        {isInstructor && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid={`item-menu-${item.id}`} className="nb-btn nb-btn-white text-sm px-2"><MoreHorizontal className="w-4 h-4" /></button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="z-[75]">
              <DropdownMenuItem data-testid={`item-edit-${item.id}`} onClick={() => setViewer("edit")}><Pencil className="w-4 h-4 mr-2" /> Edit</DropdownMenuItem>
              <DropdownMenuItem data-testid={`item-duplicate-${item.id}`} onClick={duplicate}><CopyPlus className="w-4 h-4 mr-2" /> Duplicate</DropdownMenuItem>
              <DropdownMenuItem data-testid={`item-toggle-hidden-${item.id}`} onClick={toggleHidden}>
                {item.hidden ? <><Eye className="w-4 h-4 mr-2" /> Show</> : <><EyeOff className="w-4 h-4 mr-2" /> Hide</>}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem disabled={index === 0} onClick={() => move(index - 1)}><ArrowUp className="w-4 h-4 mr-2" /> Move up</DropdownMenuItem>
              <DropdownMenuItem disabled={index === total - 1} onClick={() => move(index + 1)}><ArrowDown className="w-4 h-4 mr-2" /> Move down</DropdownMenuItem>
              {sections.length > 1 && (
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger><FolderInput className="w-4 h-4 mr-2" /> Move to section</DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="z-[75]">
                    {sections.filter((s) => s.id !== sectionId).map((s) => (
                      <DropdownMenuItem key={s.id} data-testid={`item-move-to-${item.id}-${s.id}`} onClick={() => move(10000, s.id)}>{s.title}</DropdownMenuItem>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid={`item-delete-${item.id}`} onClick={remove} className="text-red-600 focus:text-red-600"><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {viewer === "cards" && <FlashcardViewer item={item} onClose={() => setViewer(null)} />}
      {viewer === "video" && <VideoEmbed title={item.title} src={video} onClose={() => setViewer(null)} />}
      {viewer === "edit" && (
        <ItemForm mode="edit" moduleId={moduleId} item={item} materials={materials} exams={exams} projects={projects} skillTags={skillTags} onClose={() => setViewer(null)} onSaved={onChanged} />
      )}
    </li>
  );
}
